import { SMS_CONSENT_VERSION, type UserRole } from '@aslgo/shared';
import { supabase } from '../lib/supabase.js';
import type { RegFormData } from './types.js';

/** Maps the flat form bag to the snake_case `registration` object the
 *  on_auth_user_created trigger reads. */
export function buildRegistrationMetadata(role: UserRole, d: RegFormData): Record<string, unknown> {
  const agreement = {
    accepted: d.agreementAccepted ?? false,
    signature: d.fullName.trim(),
    payment_placeholder: d.paymentPlaceholder ?? '',
  };
  if (role === 'learner') {
    const reg: Record<string, unknown> = {
      age_range: d.ageRange,
      phone: d.phone ?? '',
      city: d.city ?? '',
      state: d.state ?? '',
      ethnicity: d.ethnicity ?? '',
      heard_about: d.heardAbout ?? '',
      heard_about_other: d.heardAboutOther ?? '',
      preferred_times: d.preferredTimes ?? [],
      immersion_reasons: d.immersionReasons ?? [],
      immersion_reason_other: d.immersionReasonOther ?? '',
      wants_interpreter: d.wantsInterpreter ?? false,
      interpreter_fields: d.interpreterFields ?? [],
      interpreter_field_other: d.interpreterFieldOther ?? '',
      goal: d.goal ?? '',
      plan_choice: d.planChoice ?? '',
      agreement,
    };
    if (d.ageRange === '14-17') {
      reg.guardian = {
        guardian_name: d.guardianName ?? '',
        guardian_relationship: d.guardianRelationship ?? '',
        guardian_email: d.guardianEmail?.trim() ?? '',
        guardian_phone: d.guardianPhone ?? '',
        guardian_mailing_address: d.guardianMailingAddress ?? '',
        guardian_consents: d.guardianConsents ?? {},
        guardian_signature: d.guardianSignature ?? '',
        minor_signature: d.minorSignature ?? '',
      };
    }
    return reg;
  }
  // guide
  return {
    age_range: d.ageRange,
    phone: d.phone ?? '',
    city: d.city ?? '',
    state: d.state ?? '',
    ethnicity: d.ethnicity ?? '',
    asl_years: d.aslYears ?? '',
    is_cdi: d.isCdi ?? false,
    interested_in_cdi: d.interestedInCdi ?? '',
    expertise_areas: d.expertiseAreas ?? [],
    expertise_other: d.expertiseAreaOther ?? '',
    availability_slots: d.availabilitySlots ?? [],
    sms_opt_in: d.smsConsent === 'opt_in',
    // Only an actual opt-in records which wording was agreed to; a decline
    // leaves the version null, matching how the profile route stores opt-outs.
    sms_consent_version: d.smsConsent === 'opt_in' ? SMS_CONSENT_VERSION : null,
    bg_check_consent: d.bgCheckConsent ?? false,
    code_of_conduct_consent: d.codeOfConductConsent ?? false,
    agreement,
  };
}

export async function submitRegistration(role: UserRole, d: RegFormData): Promise<{ needsEmailConfirm: boolean }> {
  const displayName = d.displayName.trim();
  const { data: status, error: rpcErr } = await supabase.rpc('check_display_name', { name: displayName });
  if (rpcErr) throw new Error(rpcErr.message);
  if (status === 'taken') throw new Error('That display name is already taken — choose a different one.');
  if (status === 'not_allowed') throw new Error("That display name isn't allowed — choose a different one.");

  const { data: signData, error: signErr } = await supabase.auth.signUp({
    email: d.email.trim(),
    password: d.password,
    options: {
      emailRedirectTo: `${window.location.origin}/`,
      data: {
        display_name: displayName,
        role,
        full_name: d.fullName.trim(),
        registration: buildRegistrationMetadata(role, d),
      },
    },
  });
  if (signErr) {
    if (/display_name|duplicate key/i.test(signErr.message)) {
      throw new Error('That display name is already taken — choose a different one.');
    }
    throw new Error(signErr.message);
  }
  return { needsEmailConfirm: !signData?.session };
}
